import React, { useState, useEffect, useRef } from 'react';
import { 
    ArrowLeft, Calendar, FileText, 
    Save, Loader2, Building, Search, X, User, MapPin, Upload, Image as ImageIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GET } from '../services/api';

const styles = `
    @keyframes fade-in-down {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    .animate-fade-in-down {
        animation: fade-in-down 0.3s ease-out forwards;
    }
`;

const StyleSheet = () => <style>{styles}</style>;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CreateMeeting = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);

    // --- Data State ---
    const [cities, setCities] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    // --- Form State ---
    const [formData, setFormData] = useState({
        city_id: '',
        chapter_id: '',
        visitor_id: '',        
        visitor_name: '',      
        visitor_image: '',     
        meeting_date: new Date().toISOString().split('T')[0], // Default to today
        remarks: '',
        location: 'my place', // Default selection
        address: '',
        latitude: '',
        longitude: ''
    });

    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [errors, setErrors] = useState({});

    // --- Google Places State ---
    const [isPlacesReady, setIsPlacesReady] = useState(false);
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
    const [addressDebounceTimer, setAddressDebounceTimer] = useState(null);
    
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);
    const addressInputRef = useRef(null);
    const sessionTokenRef = useRef(null);

    // --- 1. Fetch Cities ---
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const res = await GET('/app/cities');
                if (res && res.success) setCities(res.data);
            } catch (error) {
                console.error("Error fetching cities:", error);
            }
        };
        fetchCities();
    }, []);

    // --- 2. Fetch Chapters ---
    useEffect(() => {
        const fetchChapters = async () => {
            if (!formData.city_id) {
                setChapters([]);
                return;
            }
            try {
                const res = await GET(`/app/chapters/${formData.city_id}`);
                if (res && res.success) setChapters(res.data);
                else setChapters([]);
            } catch (error) {
                console.error("Error fetching chapters:", error);
                setChapters([]);
            }
        };
        fetchChapters();
    }, [formData.city_id]);

    // --- Load Google Places API ---
    useEffect(() => {
        const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
        if (!apiKey) return;

        if (window.google && window.google.maps && window.google.maps.places) {
            setIsPlacesReady(true);
            return;
        }

        const existingScript = document.querySelector('script[data-places-api-onetoone]');
        if (existingScript) {
            existingScript.addEventListener('load', () => setIsPlacesReady(true));
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.setAttribute('data-places-api-onetoone', 'true');
        script.onload = () => setIsPlacesReady(true);
        script.onerror = () => console.error('Failed to load Google Places API');
        document.head.appendChild(script);
    }, []);

    // --- Initialize Google Places Services ---
    useEffect(() => {
        if (!isPlacesReady) return;

        if (!autocompleteServiceRef.current) {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        }

        if (!placesServiceRef.current) {
            placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
        }

        if (!sessionTokenRef.current) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
    }, [isPlacesReady]);

    // --- 3. Visitor Search Logic ---
    useEffect(() => {
        if (formData.visitor_id) return;
        const timer = setTimeout(async () => {
            if (formData.visitor_name.length > 2) {
                setIsSearching(true);
                try {
                    const res = await GET(`/app/search-businesses?search=${formData.visitor_name}`);
                    if (res && res.success) setSearchResults(res.data);
                } catch (error) {
                    console.error("Search error:", error);
                } finally {
                    setIsSearching(false);
                }
            } else {
                setSearchResults([]);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.visitor_name, formData.visitor_id]);

    // --- Handlers ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'city_id') {
            setFormData(prev => ({ ...prev, city_id: value, chapter_id: '' }));
        } else if (name === 'visitor_name') {
            setFormData(prev => ({ ...prev, visitor_name: value, visitor_id: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSelectVisitor = (visitor) => {
        setFormData(prev => ({
            ...prev,
            visitor_id: visitor.user_id,
            visitor_name: visitor.full_name,
            visitor_image: visitor.profile_picture
        }));
        setSearchResults([]);
        if (errors.visitor_name) setErrors(prev => ({ ...prev, visitor_name: '' }));
    };

    const clearSelectedVisitor = () => {
        setFormData(prev => ({ ...prev, visitor_id: '', visitor_name: '', visitor_image: '' }));
    };

    const getAddressComponent = (components, type) => {
        const match = components?.find((c) => c.types.includes(type));
        return match ? match.long_name : '';
    };

    const fetchAddressPredictions = (value) => {
        if (!isPlacesReady || !autocompleteServiceRef.current) return;
        if (!value) {
            setAddressSuggestions([]);
            return;
        }

        const request = {
            input: value,
            componentRestrictions: { country: 'in' },
            sessionToken: sessionTokenRef.current
        };

        autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
                setAddressSuggestions([]);
                return;
            }
            setAddressSuggestions(predictions);
            setShowAddressSuggestions(true);
        });
    };

    const debounceAddressPredictions = (value) => {
        if (addressDebounceTimer) clearTimeout(addressDebounceTimer);

        const timer = setTimeout(() => {
            fetchAddressPredictions(value);
        }, 500);

        setAddressDebounceTimer(timer);
    };

    const handleAddressInputChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            address: value
        }));
        debounceAddressPredictions(value);
    };

    const handleAddressSuggestionSelect = (prediction) => {
        setShowAddressSuggestions(false);
        setAddressSuggestions([]);

        setFormData(prev => ({
            ...prev,
            address: prediction.description || prev.address
        }));

        if (!placesServiceRef.current) return;

        placesServiceRef.current.getDetails({
            placeId: prediction.place_id,
            fields: ['address_components', 'geometry', 'formatted_address'],
            sessionToken: sessionTokenRef.current
        }, (place, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;

            const lat = place.geometry?.location?.lat();
            const lng = place.geometry?.location?.lng();

            setFormData(prev => ({
                ...prev,
                latitude: typeof lat === 'number' ? lat : '',
                longitude: typeof lng === 'number' ? lng : ''
            }));

            // Reset session token after use
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        });
    };

    // Cleanup preview URL when image changes/unmounts.
    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveImage = () => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // --- Validation & Submit ---
    const validateForm = () => {
        let tempErrors = {};
        if (!formData.city_id) tempErrors.city_id = "City is required";
        if (!formData.chapter_id) tempErrors.chapter_id = "Chapter is required";
        if (!formData.visitor_id) tempErrors.visitor_name = "Select a valid visitor";
        if (!formData.meeting_date) tempErrors.meeting_date = "Date is required";
        if (!formData.location) tempErrors.location = "Location is required";
        
        // Address is required ONLY if 'other' is selected
        if (formData.location === 'other' && !formData.address.trim()) {
            tempErrors.address = "Address is required for 'Other' location";
        }
        
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);

        const payload = new FormData();
        payload.append('visitor_id', formData.visitor_id);
        payload.append('chapter_id', formData.chapter_id);
        payload.append('remarks', formData.remarks || '');
        payload.append('meeting_date', formData.meeting_date); 
        payload.append('location', formData.location);

        // Only append address and lat/long if location is 'other'
        if (formData.location === 'other') {
            payload.append('address', formData.address);
            payload.append('latitude', formData.latitude || '');
            payload.append('longitude', formData.longitude || '');
        }

        if (selectedImage) {
            payload.append('one_to_one_meeting_image', selectedImage);
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/app/create-one-to-one-metting`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}` 
                },
                body: payload
            });

            const res = await response.json();
            if (res && res.success) {
                alert("One to One Meeting Created Successfully!");
                window.location.href = '/member/dashboard';
            } else {
                alert(res?.message || "Failed to create meeting");
            }
        } catch (error) {
            console.error("Submission Error:", error);
            alert("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] font-sans pb-28 pt-6 relative">
            <StyleSheet />
            <div className="container mx-auto px-4 max-w-lg">
                <div className="flex items-center gap-4 mb-8">
                    <button onClick={() => navigate(-1)} className="p-2.5 bg-white rounded-full shadow-sm text-gray-700 hover:text-[#1A87CF] transition-colors">
                        <ArrowLeft size={22} />
                    </button>
                    <h1 className="text-xl font-bold text-gray-900">One to One Meeting</h1>
                </div>

                <div className="bg-white rounded-[30px] p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">City <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <select name="city_id" value={formData.city_id} onChange={handleChange} className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 appearance-none text-sm font-medium outline-none transition-all ${errors.city_id ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}`}>
                                        <option value="">Select City</option>
                                        {cities.map(city => <option key={city._id} value={city._id}>{city.name}</option>)}
                                    </select>
                                    <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                                {errors.city_id && <p className="text-xs text-red-500 mt-1 ml-1">{errors.city_id}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">Chapter <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <select name="chapter_id" value={formData.chapter_id} onChange={handleChange} disabled={!formData.city_id} className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 appearance-none text-sm font-medium outline-none transition-all ${errors.chapter_id ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'} ${!formData.city_id ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        <option value="">Select Chapter</option>
                                        {chapters.map(chap => <option key={chap._id} value={chap._id}>{chap.name}</option>)}
                                    </select>
                                    <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                                {errors.chapter_id && <p className="text-xs text-red-500 mt-1 ml-1">{errors.chapter_id}</p>}
                            </div>
                        </div>

                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">Visitor <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                    {formData.visitor_image ? (
                                        <img 
                                            src={formData.visitor_image} 
                                            alt="User" 
                                            className="w-6 h-6 rounded-full object-cover border border-gray-200" 
                                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} 
                                        />
                                    ) : null}
                                    <div style={{ display: formData.visitor_image ? 'none' : 'block' }}>
                                        <User size={20} />
                                    </div>
                                </div>
                                <input type="text" name="visitor_name" value={formData.visitor_name} onChange={handleChange} readOnly={!!formData.visitor_id} placeholder="Search member by name..." className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-12 pr-10 ${errors.visitor_name ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}`} />
                                {formData.visitor_id && (
                                    <button type="button" onClick={clearSelectedVisitor} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-500"><X size={16} /></button>
                                )}
                                {isSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2"><Loader2 size={18} className="animate-spin text-[#1A87CF]" /></div>}
                            </div>
                            {searchResults.length > 0 && !formData.visitor_id && (
                                <div className="absolute z-10 w-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 max-h-60 overflow-y-auto">
                                    {searchResults.map((user) => (
                                        <div key={user._id} onClick={() => handleSelectVisitor(user)} className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors">
                                            {user.profile_picture ? (
                                                <img 
                                                    src={user.profile_picture} 
                                                    alt="profile" 
                                                    className="w-10 h-10 rounded-full object-cover border border-gray-200 bg-gray-100" 
                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                />
                                            ) : null}
                                            <div className="w-10 h-10 rounded-full border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400" style={{ display: user.profile_picture ? 'none' : 'flex' }}>
                                                <User size={20} />
                                            </div>
                                            <p className="text-sm font-bold text-gray-800">{user.full_name}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {errors.visitor_name && <p className="text-xs text-red-500 mt-1 ml-1">{errors.visitor_name}</p>}
                        </div>

                        {/* Location Dropdown */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">Location <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <select 
                                    name="location" 
                                    value={formData.location} 
                                    onChange={handleChange} 
                                    className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 appearance-none text-sm font-medium outline-none transition-all ${errors.location ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}`}
                                >
                                    <option value="my place">My Place</option>
                                    <option value="your place">Your Place</option>
                                    <option value="other">Other</option>
                                </select>
                                <MapPin size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                            {errors.location && <p className="text-xs text-red-500 mt-1 ml-1">{errors.location}</p>}
                        </div>

                        {/* Address Field - Only shown if location is 'other' */}
                        {formData.location === 'other' && (
                            <div className="animate-fade-in-down space-y-4 relative z-50">
                                <div className="relative z-50">
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">Address <span className="text-red-500">*</span></label>
                                    <div className="relative z-50">
                                        <input
                                            ref={addressInputRef}
                                            type="text"
                                            value={formData.address}
                                            onChange={handleAddressInputChange}
                                            onFocus={() => {
                                                setShowAddressSuggestions(true);
                                                if (formData.address) fetchAddressPredictions(formData.address);
                                            }}
                                            onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 150)}
                                            placeholder="Search address..."
                                            className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-11 ${errors.address ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}`}
                                        />
                                        <MapPin size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>

                                        {showAddressSuggestions && addressSuggestions.length > 0 && (
                                            <ul className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-56 overflow-y-auto z-[9999]">
                                                {addressSuggestions.map((prediction) => (
                                                    <li
                                                        key={prediction.place_id}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            handleAddressSuggestionSelect(prediction);
                                                        }}
                                                        className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm text-gray-700 border-b border-gray-50 last:border-0"
                                                    >
                                                        <div className="font-semibold text-gray-700">
                                                            {prediction.structured_formatting?.main_text || prediction.description}
                                                        </div>
                                                        {prediction.structured_formatting?.secondary_text && (
                                                            <div className="text-xs text-gray-500">
                                                                {prediction.structured_formatting.secondary_text}
                                                            </div>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    {errors.address && <p className="text-xs text-red-500 mt-1 ml-1">{errors.address}</p>}
                                </div>

                                <input type="hidden" value={formData.latitude || ''} readOnly />
                                <input type="hidden" value={formData.longitude || ''} readOnly />
                            </div>
                        )}

                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">Date <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <input type="date" name="meeting_date" value={formData.meeting_date} onChange={handleChange} className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-11 ${errors.meeting_date ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}`} />
                                <Calendar size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                            </div>
                            {errors.meeting_date && <p className="text-xs text-red-500 mt-1 ml-1">{errors.meeting_date}</p>}
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">Meeting Image</label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageChange}
                                accept="image/*"
                                className="hidden"
                            />

                            {imagePreview ? (
                                <div className="rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/40 p-3">
                                    <div className="flex items-center gap-3">
                                        <img src={imagePreview} alt="Meeting" className="w-16 h-16 rounded-xl object-cover border border-blue-100" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-800 truncate">{selectedImage?.name || 'meeting-image'}</p>
                                            <p className="text-xs text-slate-500">Single image upload</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="px-3 py-2 text-xs font-semibold rounded-xl bg-white border border-slate-200 text-slate-700 hover:text-[#1A87CF]"
                                        >
                                            Change
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleRemoveImage}
                                            className="px-3 py-2 text-xs font-semibold rounded-xl bg-white border border-slate-200 text-red-500 hover:bg-red-50"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-full p-4 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 hover:border-[#1A87CF]/40 hover:bg-white transition-all flex items-center justify-center gap-2 text-sm font-semibold text-slate-600"
                                >
                                    <Upload size={18} className="text-[#1A87CF]" />
                                    Upload Meeting Image
                                    <ImageIcon size={16} className="text-slate-400" />
                                </button>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">Remarks</label>
                            <div className="relative">
                                <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="3" placeholder="Enter meeting notes..." className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 ${errors.remarks ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'} text-sm font-medium outline-none transition-all resize-none pl-11`}></textarea>
                                <FileText size={20} className="absolute left-3.5 top-4 text-gray-400"/>
                            </div>
                            {errors.remarks && <p className="text-xs text-red-500 mt-1 ml-1">{errors.remarks}</p>}
                        </div>

                        <button type="submit" disabled={loading} className="w-full py-4 bg-[#1A87CF] text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-200/50 hover:shadow-xl hover:shadow-blue-200/70 hover:-translate-y-0.5 transition-all flex justify-center items-center gap-2 mt-4">
                            {loading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                            {loading ? 'Adding Meeting...' : 'Add One to One Meeting'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateMeeting;